import { X } from 'lucide-react';
import React from 'react';
import { cn } from '@core/utils/cn';
import { EnrichedTrack, SelectedWorkout } from '../types';
import { fmtDuration, fmtKm } from '../utils/format';

interface TrackStatsCardProps {
    track: EnrichedTrack;
    workout: SelectedWorkout;
    isFocused: boolean;
    onFocus: () => void;
    onRemove: () => void;
}

const StatRow = ({ label, value }: { label: string; value: string }) => (
    <div className="flex justify-between text-[11px]">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-mono text-foreground">{value}</span>
    </div>
);

export function TrackStatsCard({
    track,
    workout,
    isFocused,
    onFocus,
    onRemove
}: TrackStatsCardProps) {
    const { stats } = track;
    return (
        <div
            onClick={onFocus}
            className={cn(
                'relative min-w-[220px] cursor-pointer rounded-lg border bg-card p-2.5 transition-colors',
                isFocused && 'border-2'
            )}
            style={{ borderColor: isFocused ? workout.color : undefined }}
            onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = workout.color;
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = isFocused
                    ? workout.color
                    : '';
            }}
        >
            <div className="mb-1 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <span
                        className="size-2.5 rounded-full"
                        style={{ backgroundColor: workout.color }}
                    />
                    <span className="text-xs font-semibold">
                        {workout.label}
                    </span>
                </div>
                <button
                    type="button"
                    aria-label="Remove"
                    onClick={(e) => {
                        e.stopPropagation();
                        onRemove();
                    }}
                    className="px-1 text-muted-foreground hover:text-red-400"
                >
                    <X className="size-3.5" />
                </button>
            </div>
            <StatRow label="Points" value={stats.n.toString()} />
            <StatRow label="Duration" value={fmtDuration(stats.duration)} />
            <StatRow label="Distance" value={`${fmtKm(stats.totalDist)} km`} />
            <StatRow
                label="Max speed"
                value={`${(stats.maxSpeed * 3.6).toFixed(2)} km/h`}
            />
            <StatRow
                label="Avg speed"
                value={`${(stats.avgSpeed * 3.6).toFixed(2)} km/h`}
            />
            <StatRow
                label="Accel min/max"
                value={`${stats.minAccel.toFixed(2)} / ${stats.maxAccel.toFixed(2)} m/s²`}
            />
            <StatRow
                label="Ele gain/loss"
                value={`${stats.eleGain.toFixed(1)} / ${stats.eleLoss.toFixed(1)} m`}
            />
        </div>
    );
}
